/**
 * BlobBody — Verlet-integrated physics body that satisfies the dynamic `Blob`
 * shape declared in `types.ts`.
 *
 * Coordinate convention: pixels, origin at canvas center, +y up.
 *
 * Spawn animation: the body stores `targetRadius` and `spawnT ∈ [0,1]`; the
 * effective rendered/collision radius is `targetRadius * easeOutCubic(spawnT)`.
 * While `spawnT < 1` the World scales gravity (both received and exerted) by
 * spawnT^3 so newborns are visually settling-in rather than yanked across
 * the canvas by neighbours.
 */
import type { Blob, BlobColor } from '../types.js';
import { clamp01, easeInCubic, easeOutCubic } from '../utils/easing.js';

export interface BlobBodyInit {
  id: string;
  position: [number, number];
  /** Radius the spawn animation eases toward. */
  targetRadius: number;
  /** Initial spawn progress (0 for a brand-new blob, 1 for an instant spawn). */
  spawnT?: number;
  /** Seconds the spawn animation runs (default 0.6). */
  spawnDuration?: number;
  mass: number;
  color: BlobColor;
  warpSeed: number;
  /** Initial vibration energy (default 0.4 — see WORLD spawn handler). */
  vibEnergy?: number;
  /** Total seconds before this blob fades out (default DEFAULT_LIFETIME_SEC). */
  lifetime?: number;
}

const DEFAULT_REFERENCE_DT = 1 / 60;
const DEFAULT_SPAWN_DURATION = 0.6;

// 要調整: 通常水滴の寿命 (seconds). Art-direction tweakable.
export const DEFAULT_LIFETIME_SEC = 30;
// 要調整: 再延命された水滴の寿命 (seconds).
export const REVIVE_LIFETIME_SEC = 20;
// 要調整: フェード開始までの残り秒 (seconds before lifetime end).
export const FADE_DURATION_SEC = 6;
// 要調整: 再延命時に縮小サイズ → フルサイズへ戻すまでの秒数.
export const REGROW_DURATION_SEC = 1.0;

export class BlobBody implements Blob {
  id: string;
  position: [number, number];
  prevPosition: [number, number];
  targetRadius: number;
  spawnT: number;
  spawnDuration: number;
  mass: number;
  color: BlobColor;
  warpSeed: number;
  vibEnergy: number;
  age: number;
  lifetime: number;
  baseTargetRadius: number;
  regrowT: number;
  regrowDuration: number;
  regrowFromRatio: number;

  private fx = 0;
  private fy = 0;
  private referenceDt = DEFAULT_REFERENCE_DT;

  constructor(init: BlobBodyInit) {
    this.id = init.id;
    this.position = [init.position[0], init.position[1]];
    this.prevPosition = [init.position[0], init.position[1]];
    this.targetRadius = init.targetRadius;
    this.spawnT = init.spawnT ?? 0;
    this.spawnDuration = init.spawnDuration ?? DEFAULT_SPAWN_DURATION;
    this.mass = init.mass;
    this.color = init.color;
    this.warpSeed = init.warpSeed;
    this.vibEnergy = init.vibEnergy ?? 0;
    this.age = 0;
    this.lifetime = init.lifetime ?? DEFAULT_LIFETIME_SEC;
    this.baseTargetRadius = init.targetRadius;
    // Regrow defaults to "inactive" — set to 0 only on revive().
    this.regrowT = 1;
    this.regrowDuration = REGROW_DURATION_SEC;
    this.regrowFromRatio = 1;
  }

  /** Currently rendered radius — eased ramp from 0 → targetRadius, with an
   *  optional regrow ramp on the revive path that lerps from
   *  `regrowFromRatio` back to 1 (suppresses the lifetime-fade modulation
   *  of targetRadius while active so the two paths can't collide). */
  get radius(): number {
    const spawnRamp = easeOutCubic(clamp01(this.spawnT));
    if (this.regrowT < 1) {
      const regrowFactor =
        this.regrowFromRatio + (1 - this.regrowFromRatio) * easeOutCubic(this.regrowT);
      return this.baseTargetRadius * regrowFactor * spawnRamp;
    }
    return this.targetRadius * spawnRamp;
  }

  /** Mass-and-attraction multiplier while spawning (cubic ramp). */
  get spawnInfluence(): number {
    const t = Math.max(0, Math.min(1, this.spawnT));
    return t * t * t;
  }

  applyForce(fx: number, fy: number): void {
    this.fx += fx;
    this.fy += fy;
  }

  integrate(dt: number): void {
    if (dt <= 0) return;
    const ax = this.fx / this.mass;
    const ay = this.fy / this.mass;

    const px = this.position[0];
    const py = this.position[1];
    const ppx = this.prevPosition[0];
    const ppy = this.prevPosition[1];

    this.prevPosition[0] = px;
    this.prevPosition[1] = py;
    this.position[0] = 2 * px - ppx + ax * dt * dt;
    this.position[1] = 2 * py - ppy + ay * dt * dt;

    this.fx = 0;
    this.fy = 0;
    this.referenceDt = dt;
  }

  /** Advance the spawn animation; clamps at 1. */
  advanceSpawn(dt: number): void {
    if (this.spawnDuration <= 0) {
      this.spawnT = 1;
      return;
    }
    this.spawnT = Math.min(1, this.spawnT + dt / this.spawnDuration);
  }

  /**
   * Advance the lifetime clock and apply the shrink-fade if we're inside the
   * fade window. When `isLastOne` is true the age (and therefore the fade
   * factor) is frozen — the blob keeps whatever shrunk targetRadius it
   * already has, preserving the "last droplet stays" art-direction rule.
   *
   * Note: targetRadius is rewritten in place so the spawn-ramp getter
   * `radius = targetRadius * easeOutCubic(spawnT)` remains unchanged. This
   * gives us a clean two-stage composition (spawn ramp × shrink factor).
   */
  tickAge(dt: number, isLastOne: boolean): void {
    if (isLastOne) return; // freeze
    this.age += dt;
    // Regrow ramp progresses in parallel with age — the new lifetime
    // clock is already running, but the visual ease back to full size
    // takes ~regrowDuration seconds.
    if (this.regrowT < 1 && this.regrowDuration > 0) {
      this.regrowT = Math.min(1, this.regrowT + dt / this.regrowDuration);
    }
    const fadeStart = this.lifetime - FADE_DURATION_SEC;
    if (this.age >= fadeStart && FADE_DURATION_SEC > 0) {
      const fadeProgress = clamp01((this.age - fadeStart) / FADE_DURATION_SEC);
      const shrink = 1 - easeInCubic(fadeProgress);
      this.targetRadius = this.baseTargetRadius * shrink;
    } else {
      this.targetRadius = this.baseTargetRadius;
    }
  }

  /** Reset age & restore size — used by World when a new blob is added while
   *  this one was the lone survivor. Captures the pre-revive shrink ratio
   *  (targetRadius / baseTargetRadius, excluding spawn ramp) so the radius
   *  getter can lerp continuously from the shrunk size back to full over
   *  regrowDuration seconds — no instantaneous jump. */
  revive(): void {
    const ratio = this.baseTargetRadius > 0
      ? clamp01(this.targetRadius / this.baseTargetRadius)
      : 1;
    this.regrowFromRatio = ratio;
    this.regrowT = 0;
    this.regrowDuration = REGROW_DURATION_SEC;
    this.age = 0;
    this.lifetime = REVIVE_LIFETIME_SEC;
    this.targetRadius = this.baseTargetRadius;
  }

  /** True when the lifetime clock has expired and the blob should be
   *  removed (defensive — World also gates on isLastOne). */
  get isExpired(): boolean {
    return this.age >= this.lifetime;
  }

  setReferenceDt(dt: number): void {
    if (dt > 0) this.referenceDt = dt;
  }

  getVelocity(): [number, number] {
    const dt = this.referenceDt > 0 ? this.referenceDt : DEFAULT_REFERENCE_DT;
    return [
      (this.position[0] - this.prevPosition[0]) / dt,
      (this.position[1] - this.prevPosition[1]) / dt,
    ];
  }

  setVelocity(v: [number, number]): void {
    const dt = this.referenceDt > 0 ? this.referenceDt : DEFAULT_REFERENCE_DT;
    this.prevPosition[0] = this.position[0] - v[0] * dt;
    this.prevPosition[1] = this.position[1] - v[1] * dt;
  }

  setPosition(x: number, y: number): void {
    const vx = this.position[0] - this.prevPosition[0];
    const vy = this.position[1] - this.prevPosition[1];
    this.position[0] = x;
    this.position[1] = y;
    this.prevPosition[0] = x - vx;
    this.prevPosition[1] = y - vy;
  }
}
